import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isDate } from 'date-fns';
import {
  AllCommentsGenericDocument,
  useAllCommentsGenericQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as kpiState from '../state';
import {
  COMMENT_METADATA_TYPE_QAQC_JOB_PHASE_REMARKS,
  KPI_LOG_ENTRY_ATTACHMENTS_COLLECTION_NAME,
  KPI_NOTE_LOG_METADATA_KEY,
  KPI_NOTE_LOG_SHIFT_METADATA_KEY,
  QAQC_JOB_PHASE_REMARKS_COMMENT_KEY,
} from '../constants';
import { formatDateToYYYYMMDD, isSameCalendarDay } from '../utils';

type UseJobPhaseRemarksOptions = {
  logEntryId: string | undefined;
};

export const useJobPhaseRemarks = ({ logEntryId }: UseJobPhaseRemarksOptions) => {
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const structureId = useGetStructureId();

  const [remarksText, setRemarksText] = useState('');

  const [createComment, { loading: createLoading }] = useCreateCommentMutation({
    awaitRefetchQueries: true,
    refetchQueries: [AllCommentsGenericDocument],
  });
  const [updateComment, { loading: updateLoading }] = useUpdateCommentMutation({
    awaitRefetchQueries: true,
    refetchQueries: [AllCommentsGenericDocument],
  });

  const { data: commentsData } = useAllCommentsGenericQuery({
    variables: {
      input: {
        collectionName: KPI_LOG_ENTRY_ATTACHMENTS_COLLECTION_NAME,
        referenceId: logEntryId ?? '',
        structureId,
      },
    },
    skip: !logEntryId || !structureId,
  });

  const comment = useMemo(() => {
    if (!commentsData?.allComments) {
      return undefined;
    }
    const forShift = commentsData.allComments.filter((item) => {
      const date = new Date(item?.metadata?.[KPI_NOTE_LOG_METADATA_KEY]);
      if (!isDate(date)) {
        return false;
      }
      const isSameDate = isSameCalendarDay(date, selectedDate);
      const commentShift = item?.metadata?.[KPI_NOTE_LOG_SHIFT_METADATA_KEY];
      const isSameShift = commentShift === selectedShift;
      return isSameDate && isSameShift;
    });
    return forShift.find((item) => item.key === QAQC_JOB_PHASE_REMARKS_COMMENT_KEY);
  }, [commentsData?.allComments, selectedDate, selectedShift]);

  useEffect(() => {
    setRemarksText(comment?.text ?? '');
  }, [comment?.id, comment?.text]);

  const remarksDirty = remarksText !== (comment?.text ?? '');

  const remarksTextRef = useRef(remarksText);
  remarksTextRef.current = remarksText;
  const commentRef = useRef(comment);
  commentRef.current = comment;

  const resetRemarksFromServer = useCallback(() => {
    setRemarksText(comment?.text ?? '');
  }, [comment?.text]);

  const persistJobPhaseRemarks = useCallback(
    async (targetLogEntryId: string) => {
      if (!structureId) {
        return;
      }
      const text = remarksTextRef.current;
      const currentComment = commentRef.current;
      const dirty = text !== (currentComment?.text ?? '');
      if (!dirty) {
        return;
      }
      if (!currentComment && !text.trim()) {
        return;
      }

      if (currentComment) {
        await updateComment({
          variables: {
            input: {
              id: currentComment.id,
              text,
              structureId,
            },
          },
        });
      } else {
        await createComment({
          variables: {
            input: {
              collectionName: KPI_LOG_ENTRY_ATTACHMENTS_COLLECTION_NAME,
              referenceId: targetLogEntryId,
              structureId,
              text,
              key: QAQC_JOB_PHASE_REMARKS_COMMENT_KEY,
              metadata: {
                [KPI_NOTE_LOG_METADATA_KEY]: formatDateToYYYYMMDD(selectedDate),
                [KPI_NOTE_LOG_SHIFT_METADATA_KEY]: selectedShift,
                type: COMMENT_METADATA_TYPE_QAQC_JOB_PHASE_REMARKS,
              },
            },
          },
        });
      }
    },
    [structureId, updateComment, createComment, selectedDate, selectedShift]
  );

  const remarksPersistLoading = createLoading || updateLoading;

  return {
    remarksText,
    setRemarksText,
    remarksDirty,
    resetRemarksFromServer,
    persistJobPhaseRemarks,
    remarksPersistLoading,
  };
};
